/**
 * Cloudinary delivery helpers · belt-and-braces to the account-wide
 * "Format = Auto · Quality = Auto" default.
 *
 * `cldOpt(url, opts?)`
 *   Idempotently injects `f_auto,q_auto` (and optionally `w_<n>`,
 *   `dpr_auto`, `q_<override>`) into any Cloudinary delivery URL by
 *   inserting a transformation segment right after `/image/upload/`
 *   or `/video/upload/`. Safe on:
 *     · non-Cloudinary URLs           (returned unchanged)
 *     · falsy / non-string values     (returned unchanged)
 *     · URLs that already carry these transformations (no double-apply)
 *     · URLs that already have a leading transformation segment
 *       (we merge our params into the existing segment)
 *
 * Design ruling 06 Jul 2026: never touch originals · never re-encode ·
 * only mutate the *delivery* URL. Reversible any time by removing the
 * segment or the account-level default.
 *
 *   Examples:
 *     cldOpt("https://res.cloudinary.com/x/image/upload/v1/foo.jpg")
 *       → "…/image/upload/f_auto,q_auto/v1/foo.jpg"
 *
 *     cldOpt(url, { w: 800 })
 *       → "…/image/upload/f_auto,q_auto,w_800,dpr_auto/v1/foo.jpg"
 *
 *     cldOpt(url, { q: "auto:best" })
 *       → "…/image/upload/f_auto,q_auto:best/v1/foo.jpg"
 */

const CLD_UPLOAD_RE = /(\/(?:image|video)\/upload\/)([^/]*)(\/)/;

function paramKey(p) {
  // f_auto / q_auto / q_auto:best / w_800 / dpr_auto → "f" / "q" / "w" / "dpr"
  const m = /^([a-z]+)_/i.exec(p);
  return m ? m[1].toLowerCase() : p.toLowerCase();
}

/**
 * Merge our params into an existing transformation segment (or return
 * a new one if the segment is versioned like `v123` — which means the
 * URL has no transformation segment yet).
 */
function mergeParams(existingSeg, wantParams) {
  // If existingSeg starts with `v` followed by digits, it's a version
  // token, not a transformation. In that case, our params become the
  // *new* transformation segment and the version is preserved intact.
  if (/^v\d+$/.test(existingSeg)) {
    return { transform: wantParams.join(","), keepExisting: true };
  }
  const have = existingSeg ? existingSeg.split(",").filter(Boolean) : [];
  const haveKeys = new Set(have.map(paramKey));
  const merged = [...have];
  for (const p of wantParams) {
    if (!haveKeys.has(paramKey(p))) merged.push(p);
  }
  return { transform: merged.join(","), keepExisting: false };
}

export function cldOpt(url, opts = {}) {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("res.cloudinary.com")) return url;
  // Fetch/URL raw uploads (rare) don't need this — only /upload/ paths.
  const m = CLD_UPLOAD_RE.exec(url);
  if (!m) return url;

  const { w, q, dpr } = opts;
  const wantParams = ["f_auto"];
  wantParams.push(q ? `q_${q}` : "q_auto");
  if (typeof w === "number" && w > 0) wantParams.push(`w_${Math.round(w)}`);
  if (dpr) wantParams.push(`dpr_${dpr}`);
  else if (typeof w === "number") wantParams.push("dpr_auto");

  const before = url.slice(0, m.index) + m[1];      // "…/image/upload/"
  const existingSeg = m[2];                          // "" or "f_auto,q_auto" or "v123"
  const after = url.slice(m.index + m[0].length - 1); // starts with "/"

  const { transform, keepExisting } = mergeParams(existingSeg, wantParams);

  if (keepExisting) {
    // Existing segment was a version token → insert transform before it.
    return `${before}${transform}/${existingSeg}${after}`;
  }
  return `${before}${transform}${after}`;
}

/**
 * Convenience: build a Cloudinary `srcset` for responsive images from
 * a single URL. Widths default to a sensible 5-tier ladder.
 */
export function cldSrcSet(url, widths = [480, 768, 1024, 1440, 1920]) {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com")) return undefined;
  return widths.map((w) => `${cldOpt(url, { w })} ${w}w`).join(", ");
}
